import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Wand2, Send, RotateCcw, Save, CheckCircle2, Loader2, Sparkles,
  AlertCircle, ClipboardCopy, X,
} from "lucide-react";
import { GlassPanel } from "./GlassPanel";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Skill Builder — a guided interview that walks the admin through 15+
 * shop-specific questions and then compiles the answers into a full
 * quoting skill markdown that can replace the default one.
 *
 * Backed by /api/quote-agent/skill-builder/{chat,session,reset}. The
 * generated skill is only persisted to the actual quoting agent's system
 * prompt when the admin explicitly clicks "Save as my shop's quoting
 * skill" (which calls PUT /api/quote-agent/skill under the hood).
 */
export default function SkillBuilderPanel() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [generatedSkill, setGeneratedSkill] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [starting, setStarting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore session on mount so a refresh doesn't lose interview progress.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quote-agent/skill-builder/session");
        setMessages(data.messages || []);
        setGeneratedSkill(data.generated_skill || null);
      } catch { /* first-run, nothing yet */ }
    })();
  }, []);

  // Auto-scroll to newest message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const startInterview = async () => {
    if (starting || busy) return;
    setStarting(true);
    setBusy(true);
    try {
      const { data } = await api.post("/quote-agent/skill-builder/chat", { message: "" });
      setMessages([{ role: "assistant", content: data.reply, created_at: new Date().toISOString() }]);
      setGeneratedSkill(data.generated_skill || null);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start interview");
    } finally {
      setBusy(false);
      setStarting(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: v, created_at: new Date().toISOString() }]);
    setText("");
    try {
      const { data } = await api.post("/quote-agent/skill-builder/chat", { message: v });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, created_at: new Date().toISOString() }]);
      if (data.generated_skill) {
        setGeneratedSkill(data.generated_skill);
        toast.success("Skill draft ready — review and save it below.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Interview step failed — please try again";
      toast.error(msg);
      setMessages((m) => [...m, {
        role: "assistant",
        content: `⚠️ ${msg}`,
        created_at: new Date().toISOString(),
        _error: true,
      }]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  };

  const reset = async () => {
    try {
      await api.post("/quote-agent/skill-builder/reset");
      setMessages([]);
      setGeneratedSkill(null);
      setText("");
      toast.success("Interview reset — start fresh whenever you're ready.");
    } catch (err) {
      toast.error("Failed to reset");
    } finally {
      setConfirmReset(false);
    }
  };

  const saveSkill = async () => {
    if (!generatedSkill) return;
    try {
      await api.put("/quote-agent/skill", { content: generatedSkill });
      toast.success("Saved as your shop's quoting skill. New chats will use it.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save skill");
    } finally {
      setConfirmSave(false);
    }
  };

  const copySkill = async () => {
    if (!generatedSkill) return;
    try {
      await navigator.clipboard.writeText(generatedSkill);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — select and copy manually");
    }
  };

  return (
    <GlassPanel className="!p-6 max-w-3xl" data-testid="settings-skill-builder">
      <div className="mb-4">
        <div className="text-label flex items-center gap-2">
          <Wand2 className="w-3 h-3" /> Quoting skill wizard
        </div>
        <h2 className="font-heading text-2xl text-white mt-1">Skill Builder</h2>
        <p className="text-sm text-slate-400 mt-1 max-w-xl">
          Walk through a 15+ question interview with Claude about how <em>your</em> shop quotes work — services, materials, price ranges, business rules, upsells, tone — and get a bespoke quoting agent prompt built from your answers. Nothing is saved to the live agent until you click <b>Save as my shop&apos;s quoting skill</b>.
        </p>
      </div>

      {/* Kick-off card — shown until the interview has any messages. */}
      {messages.length === 0 && (
        <div
          data-testid="skill-builder-empty"
          className="rounded-3xl border border-dashed border-white/15 bg-white/[0.02] p-6 text-center space-y-3"
        >
          <div className="mx-auto w-12 h-12 rounded-2xl bg-violet-500/15 border border-violet-400/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-violet-300" />
          </div>
          <div className="text-white font-heading text-lg">Rebuild your quoting brain</div>
          <div className="text-sm text-slate-400 max-w-md mx-auto">
            The wizard asks about your shop identity, services, price ranges per vehicle class, materials you stock, business rules, upsells and tone — roughly 15-22 short questions, one at a time.
          </div>
          <button
            type="button"
            data-testid="skill-builder-start"
            disabled={starting}
            onClick={startInterview}
            className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-100 disabled:opacity-50"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {starting ? "Starting…" : "Start interview"}
          </button>
        </div>
      )}

      {/* Live transcript + composer */}
      {messages.length > 0 && (
        <div className="space-y-3">
          <div
            ref={scrollRef}
            data-testid="skill-builder-messages"
            className="max-h-[420px] overflow-y-auto space-y-3 bg-black/40 border border-white/10 rounded-2xl p-4"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-violet-500/20 border border-violet-400/30 text-white"
                    : m._error
                      ? "bg-red-500/10 border border-red-400/30 text-red-100"
                      : "bg-white/[0.04] border border-white/10 text-slate-200"
                }`}>
                  {m.role === "assistant" ? stripSkillBlock(m.content) : m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/[0.04] border border-white/10 text-slate-400 px-3 py-2 rounded-2xl text-sm inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="flex gap-2 items-center">
            <input
              ref={inputRef}
              data-testid="skill-builder-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Answer the current question…"
              disabled={busy}
              className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="skill-builder-send"
              disabled={busy || !text.trim()}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-black disabled:opacity-40 hover:bg-slate-100"
              title="Send answer"
            >
              <Send className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="skill-builder-reset"
              onClick={() => setConfirmReset(true)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"
              title="Reset the interview"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* Generated skill card — shown only once Claude has produced the
          final skill block. Admin can preview, copy, or save. */}
      {generatedSkill && (
        <div
          data-testid="skill-builder-generated"
          className="mt-4 rounded-3xl border border-emerald-400/30 bg-emerald-500/10 p-4 space-y-3"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-label flex items-center gap-2 text-emerald-200">
                <CheckCircle2 className="w-3 h-3" /> Draft ready
              </div>
              <div className="font-heading text-lg text-white mt-1">Your bespoke quoting skill</div>
              <div className="text-xs text-slate-400 mt-1 max-w-md">
                Preview below. Saving replaces the current quoting agent&apos;s system prompt. You can re-run this wizard anytime to rebuild.
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                data-testid="skill-builder-preview"
                onClick={() => setPreviewOpen(true)}
                className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white px-3 py-1.5 rounded-full text-xs hover:bg-white/10"
              >
                Preview
              </button>
              <button
                type="button"
                data-testid="skill-builder-copy"
                onClick={copySkill}
                className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white px-3 py-1.5 rounded-full text-xs hover:bg-white/10"
              >
                <ClipboardCopy className="w-3 h-3" /> Copy
              </button>
              <button
                type="button"
                data-testid="skill-builder-save"
                onClick={() => setConfirmSave(true)}
                className="inline-flex items-center gap-1.5 bg-emerald-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold hover:bg-emerald-400"
              >
                <Save className="w-3 h-3" /> Save as my shop&apos;s quoting skill
              </button>
            </div>
          </div>
          <pre className="text-[11px] leading-relaxed text-slate-200 bg-black/40 border border-white/10 rounded-2xl p-3 max-h-40 overflow-y-auto font-mono whitespace-pre-wrap">
            {generatedSkill.slice(0, 900)}{generatedSkill.length > 900 ? "…" : ""}
          </pre>
        </div>
      )}

      {/* Preview modal — full markdown of the generated skill */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="glass-strong rounded-3xl p-6 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <div>
                <div className="text-label">Generated skill</div>
                <h3 className="font-heading text-xl text-white mt-1">Full preview</h3>
              </div>
              <button
                type="button"
                data-testid="skill-builder-preview-close"
                onClick={() => setPreviewOpen(false)}
                className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <pre className="text-xs leading-relaxed text-slate-200 bg-black/40 border border-white/10 rounded-2xl p-4 overflow-y-auto font-mono whitespace-pre-wrap flex-1">
              {generatedSkill}
            </pre>
          </div>
        </div>
      )}

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent data-testid="skill-builder-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset the interview?</AlertDialogTitle>
            <AlertDialogDescription>
              This wipes every answer so far. Any generated draft skill will also be cleared. Your live quoting skill isn&apos;t affected until you click Save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skill-builder-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="skill-builder-reset-confirm" onClick={reset}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmSave} onOpenChange={setConfirmSave}>
        <AlertDialogContent data-testid="skill-builder-save-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the live quoting skill?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This overwrites your current Quoting Agent system prompt with the one Claude just built. Every future Auto-quote and manual chat will use the new prompt.
              </span>
              <span className="block text-amber-300 text-xs inline-flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" /> You can re-run the wizard anytime, or reset to the built-in default from the Quoting Agent skill editor.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skill-builder-save-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="skill-builder-save-confirm" onClick={saveSkill}>
              Save as live skill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </GlassPanel>
  );
}

// Hide the ```skill``` block inside the chat — it renders in its own
// "Draft ready" card below so the transcript stays readable.
function stripSkillBlock(text) {
  return text.replace(/```skill\s*\n[\s\S]*?```/i, "").trim();
}
